import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader } from "@/components/AppLayout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Table2, Sparkles, ArrowLeft } from "lucide-react";

const fmtVal = (v, unit) => {
  if (v === null || v === undefined) return "—";
  if (unit === "currency") {
    const a = Math.abs(v);
    if (a >= 1000000) return `$${(v / 1000000).toFixed(v % 1000000 === 0 ? 0 : 2)}M`;
    if (a >= 10000) return `$${Math.round(v / 1000)}k`;
    return `$${Math.round(v).toLocaleString()}`;
  }
  if (unit === "percent") return `${v}%`;
  return Number.isInteger(v) ? v.toLocaleString() : v.toFixed(1);
};
const fmtGoal = (m) => `${m.goal_op === "lte" ? "≤" : "≥"} ${fmtVal(m.goal, m.unit)}`;
const wkLabel = (iso) => new Date(iso + "T00:00:00").toLocaleDateString("en-AU", { day: "numeric", month: "short" });

const PERIODS = [["weekly", "Weekly"], ["monthly", "Monthly"], ["quarterly", "Quarterly"], ["ytd", "YTD (annual)"]];
const PERIOD_HINT = {
  weekly: "Week starting", monthly: "Month of", quarterly: "Quarter starting", ytd: "Financial year starting",
};
const EMPTY = { name: "", description: "", source: "manual", unit: "count", goal_op: "gte", goal: "", period: "weekly", owner_id: "", owner_name: "Leadership" };

const Badge = ({ m }) => (
  <span className={`inline-block mt-1 text-[9px] font-bold tracking-wider px-1.5 py-0.5 rounded-sm ${m.source === "manual" ? "bg-slate-100 text-slate-500" : "bg-slate-900 text-cyan-300"}`}>
    {m.source === "manual" ? "MANUAL" : m.source === "custom" ? "AUTO · AI" : "AUTO"}
  </span>
);

const MetricCell = ({ m, onEdit }) => (
  <td className="px-4 py-2">
    <div className="flex items-start gap-2">
      <div className="min-w-0">
        <div className="font-semibold text-slate-900 leading-tight">{m.name}</div>
        {m.description && <div className="text-[10.5px] text-slate-400 truncate max-w-[180px] mt-0.5">{m.description}</div>}
        <Badge m={m} />
      </div>
      <button className="text-slate-400 hover:text-cyan-600 mt-0.5" onClick={() => onEdit(m)} data-testid={`edit-metric-${m.id}`}><Pencil size={13} /></button>
    </div>
  </td>
);

function SectionHeader({ title, chip, note }) {
  return (
    <div className="flex items-center gap-2.5 px-4 py-2 bg-slate-900 rounded-t-sm">
      <span className="text-[11px] font-bold tracking-[0.18em] uppercase text-white">{title}</span>
      <span className="text-[9px] font-bold tracking-wider uppercase px-2 py-0.5 rounded-full bg-cyan-600 text-white">{chip}</span>
      <span className="ml-auto text-[10.5px] text-slate-400 hidden sm:block">{note}</span>
    </div>
  );
}

function PeriodTable({ section, title, chip, note, columns, metrics, onEdit, onCell, emptyHint }) {
  return (
    <Card className="rounded-sm border-slate-200 shadow-none overflow-x-auto mb-5" data-testid={`section-${section}`}>
      <SectionHeader title={title} chip={chip} note={note} />
      {metrics.length === 0 ? (
        <div className="px-4 py-5 text-xs text-slate-400" data-testid={`section-${section}-empty`}>{emptyHint}</div>
      ) : (
        <table className="w-full text-sm min-w-[900px]">
          <thead>
            <tr className="text-[10px] uppercase tracking-wider text-slate-400 border-b border-slate-200">
              <th className="text-left px-4 py-2 font-semibold min-w-[200px]">Metric</th>
              <th className="text-left px-2 py-2 font-semibold">Owner</th>
              <th className="text-center px-2 py-2 font-semibold">Goal</th>
              <th className="text-center px-2 py-2 font-semibold">Avg</th>
              {columns.map((c) => (
                <th key={c.start} className={`text-center px-2 py-2 font-semibold whitespace-nowrap ${c.current ? "text-cyan-700" : ""}`}>
                  {c.label}{c.current ? " ◀" : ""}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {metrics.map((m) => (
              <tr key={m.id} className="border-b border-slate-100" data-testid={`metric-row-${m.id}`}>
                <MetricCell m={m} onEdit={onEdit} />
                <td className="px-2 py-2 text-xs text-slate-500 whitespace-nowrap">{m.owner_name}</td>
                <td className="px-2 py-2 text-center font-mono text-xs text-slate-700 whitespace-nowrap">{fmtGoal(m)}</td>
                <td className="px-2 py-2 text-center font-mono text-xs text-slate-500">{fmtVal(m.avg, m.unit)}</td>
                {columns.map((c, i) => {
                  const v = m.values[i];
                  const hit = m.hits[i];
                  const notStarted = c.started === false;
                  const cls = notStarted || v == null ? "text-slate-300" : hit ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700";
                  const editable = m.source === "manual" && !notStarted;
                  return (
                    <td key={c.start}
                      data-testid={`cell-${m.id}-${c.start}`}
                      onClick={() => editable && onCell({ metric: m, week: c.start, value: v ?? "" })}
                      className={`px-2 py-2 text-center font-mono text-xs ${cls} ${c.current ? "ring-2 ring-inset ring-cyan-600" : ""} ${editable ? "cursor-pointer hover:ring-1 hover:ring-slate-300" : ""}`}>
                      {notStarted ? "—" : fmtVal(v, m.unit)}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  );
}

function YtdSection({ ytd, onEdit, onCell }) {
  const metrics = ytd?.metrics || [];
  return (
    <Card className="rounded-sm border-slate-200 shadow-none overflow-x-auto mb-5" data-testid="section-ytd">
      <SectionHeader title="YTD" chip={`1 Jul → today · ${ytd?.label || ""}`}
        note={`annual goals — black tick = where you should be, ${ytd?.elapsed_pct ?? 0}% through the year`} />
      {metrics.length === 0 ? (
        <div className="px-4 py-5 text-xs text-slate-400" data-testid="section-ytd-empty">
          No annual KPIs yet — add a metric with period "YTD (annual)", or build them from the Annual Goals page.
        </div>
      ) : (
        <table className="w-full text-sm min-w-[900px]">
          <thead>
            <tr className="text-[10px] uppercase tracking-wider text-slate-400 border-b border-slate-200">
              <th className="text-left px-4 py-2 font-semibold min-w-[200px]">Metric</th>
              <th className="text-left px-2 py-2 font-semibold">Owner</th>
              <th className="text-center px-2 py-2 font-semibold">Annual goal</th>
              <th className="text-center px-2 py-2 font-semibold">YTD actual</th>
              <th className="text-left px-2 py-2 font-semibold min-w-[200px]">Progress</th>
              <th className="text-center px-2 py-2 font-semibold">Pace</th>
            </tr>
          </thead>
          <tbody>
            {metrics.map((m) => {
              const editable = m.source === "manual";
              const prog = Math.max(0, Math.min(100, m.progress_pct ?? 0));
              return (
                <tr key={m.id} className="border-b border-slate-100" data-testid={`ytd-row-${m.id}`}>
                  <MetricCell m={m} onEdit={onEdit} />
                  <td className="px-2 py-2 text-xs text-slate-500 whitespace-nowrap">{m.owner_name}</td>
                  <td className="px-2 py-2 text-center font-mono text-xs text-slate-700 whitespace-nowrap">{fmtGoal(m)}</td>
                  <td data-testid={`ytd-value-${m.id}`}
                    onClick={() => editable && onCell({ metric: m, week: ytd.start, value: m.value ?? "" })}
                    className={`px-2 py-2 text-center font-mono text-xs ${m.value == null ? "text-slate-300" : m.on_pace ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"} ${editable ? "cursor-pointer hover:ring-1 hover:ring-slate-300" : ""}`}>
                    {fmtVal(m.value, m.unit)}
                  </td>
                  <td className="px-2 py-2">
                    <div className="relative h-2 bg-slate-200 rounded-full overflow-visible min-w-[170px]">
                      <div className={`absolute left-0 top-0 bottom-0 rounded-full ${m.on_pace ? "bg-emerald-600" : "bg-red-600"}`} style={{ width: `${prog}%` }} />
                      {m.unit !== "percent" && <div className="absolute w-0.5 bg-slate-900 -top-0.5 -bottom-0.5" style={{ left: `${Math.min(100, ytd?.elapsed_pct ?? 0)}%` }} />}
                    </div>
                  </td>
                  <td className="px-2 py-2 text-center whitespace-nowrap">
                    {m.on_pace == null ? <span className="text-slate-300 text-xs">—</span> : (
                      <span className={`text-[10px] font-bold uppercase tracking-wider ${m.on_pace ? "text-emerald-700" : "text-red-700"}`} data-testid={`ytd-pace-${m.id}`}>
                        {m.on_pace ? "On pace" : "Behind"}{m.progress_pct != null ? ` · ${m.progress_pct}%` : ""}
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </Card>
  );
}

export default function ManagementScorecard() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const [editing, setEditing] = useState(null);
  const [cell, setCell] = useState(null); // {metric, week, value}
  const [ai, setAi] = useState(null);

  const { data } = useQuery({ queryKey: ["mgmt-scorecard"], queryFn: async () => (await api.get("/mgmt/scorecard?weeks=8")).data });
  const { data: connectors } = useQuery({ queryKey: ["mgmt-connectors"], queryFn: async () => (await api.get("/mgmt/metric-connectors")).data });
  const { data: team } = useQuery({ queryKey: ["team"], queryFn: async () => (await api.get("/auth/team")).data });

  const weeks = (data?.weeks || []).map((w) => ({ ...w, label: wkLabel(w.start) }));
  const invalidate = () => qc.invalidateQueries({ queryKey: ["mgmt-scorecard"] });

  const saveMut = useMutation({
    mutationFn: async (f) => {
      const payload = {
        name: f.name, description: f.description || null, unit: f.unit,
        goal_op: f.goal_op, goal: parseFloat(f.goal),
        owner_id: f.owner_id || null, owner_name: f.owner_name,
      };
      if (f.id) return (await api.patch(`/mgmt/metrics/${f.id}`, payload)).data;
      return (await api.post("/mgmt/metrics", { ...payload, source: f.source, period: f.period })).data;
    },
    onSuccess: () => { invalidate(); setEditing(null); toast.success("Metric saved"); },
    onError: (e) => toast.error(e.response?.data?.detail || "Could not save metric"),
  });

  const delMut = useMutation({
    mutationFn: async (id) => (await api.delete(`/mgmt/metrics/${id}`)).data,
    onSuccess: () => { invalidate(); setEditing(null); toast.success("Metric deleted"); },
    onError: (e) => toast.error(e.response?.data?.detail || "Could not delete"),
  });

  const valMut = useMutation({
    mutationFn: async ({ metric, week, value }) =>
      (await api.put(`/mgmt/metrics/${metric.id}/value`, { week_start: week, value })).data,
    onSuccess: () => { invalidate(); setCell(null); },
    onError: (e) => toast.error(e.response?.data?.detail || "Could not save value"),
  });

  const buildMut = useMutation({
    mutationFn: async (prompt) => (await api.post("/mgmt/metrics/ai-build", { prompt })).data,
    onSuccess: (d) => {
      if (!d.feasible) return setAi((a) => ({ ...a, error: d.reason, proposal: null }));
      setAi((a) => ({
        ...a, error: null, explanation: d.explanation, preview: d.preview,
        proposal: { ...d.proposal, goal: String(d.proposal.goal), period: d.proposal.period || "weekly", owner_id: "", owner_name: "Leadership" },
      }));
    },
    onError: (e) => setAi((a) => ({ ...a, error: e.response?.data?.detail || "AI build failed — try again" })),
  });

  const addAiMut = useMutation({
    mutationFn: async () => {
      const p = ai.proposal;
      return (await api.post("/mgmt/metrics", {
        name: p.name, description: p.description || null, source: "custom",
        unit: p.unit, goal_op: p.goal_op, goal: parseFloat(p.goal), period: p.period || "weekly",
        owner_id: p.owner_id || null, owner_name: p.owner_name || "Leadership",
        query_def: p.query_def,
      })).data;
    },
    onSuccess: () => { invalidate(); setAi(null); toast.success("AI metric added to the scorecard"); },
    onError: (e) => toast.error(e.response?.data?.detail || "Could not add metric"),
  });

  const setProp = (p) => setAi((a) => ({ ...a, proposal: { ...a.proposal, ...p } }));
  const openNew = () => setEditing({ ...EMPTY });
  const openEdit = (m) => setEditing({ ...m, description: m.description || "", goal: String(m.goal), owner_id: m.owner_id || "", period: m.period || "weekly" });
  const setForm = (p) => setEditing((f) => ({ ...f, ...p }));

  const pickSource = (key) => {
    if (key === "manual") return setForm({ source: "manual" });
    const c = (connectors || []).find((x) => x.key === key);
    setForm({ source: key, name: c?.label || "", description: c?.desc || "", unit: c?.unit || "count", goal_op: c?.goal_op || "gte", goal: String(c?.default_goal ?? "") });
  };

  return (
    <div data-testid="management-scorecard-page" className="pb-24">
      <PageHeader
        title="KPI Scorecard — by Reporting Period"
        subtitle={`${data?.ytd?.label || "FY"} (Jul–Jun) · live data sectioned Weekly / Monthly / Quarterly / YTD · each KPI is assigned a period when created`}
        actions={
          <div className="flex gap-2">
            {isSuper && (
              <Button variant="outline" onClick={() => setAi({ prompt: "", error: null, proposal: null })}
                className="rounded-sm border-slate-300 text-slate-700 hover:text-cyan-700 hover:border-cyan-400" data-testid="ai-builder-btn">
                <Sparkles size={15} className="mr-1 text-cyan-600" />AI Builder
              </Button>
            )}
            <Button onClick={openNew} className="bg-cyan-600 hover:bg-cyan-700 rounded-sm" data-testid="add-metric-btn"><Plus size={15} className="mr-1" />Add Metric</Button>
          </div>
        }
      />
      <div className="px-4 sm:px-6 lg:px-10">
        <PeriodTable section="weekly" title="Weekly" chip="Mon–Sun" onEdit={openEdit} onCell={setCell}
          note="operational pulse — reviewed in the Level 10 · 2 misses in a row auto-drops an Issue"
          columns={weeks} metrics={data?.metrics || []}
          emptyHint='No weekly KPIs yet — add one with period "Weekly".' />
        <PeriodTable section="monthly" title="Monthly" chip="Calendar months" onEdit={openEdit} onCell={setCell}
          note="tactical trends — manual owners nudged the first Monday of the month"
          columns={data?.monthly?.columns || []} metrics={data?.monthly?.metrics || []}
          emptyHint='No monthly KPIs yet — add one with period "Monthly".' />
        <PeriodTable section="quarterly" title="Quarterly" chip="FY quarters · Jul–Jun" onEdit={openEdit} onCell={setCell}
          note="strategic measures — aligned with your Rocks quarters"
          columns={data?.quarterly?.columns || []} metrics={data?.quarterly?.metrics || []}
          emptyHint='No quarterly KPIs yet — add one with period "Quarterly".' />
        <YtdSection ytd={data?.ytd} onEdit={openEdit} onCell={setCell} />
        <div className="flex flex-wrap items-center gap-4 text-[11px] text-slate-500">
          <span><i className="inline-block w-3 h-3 bg-emerald-100 rounded-sm align-[-2px] mr-1" />Hit goal</span>
          <span><i className="inline-block w-3 h-3 bg-red-100 rounded-sm align-[-2px] mr-1" />Missed</span>
          <span><i className="inline-block w-3 h-3 border-2 border-cyan-600 rounded-sm align-[-2px] mr-1" />Current period</span>
          <span><i className="inline-block w-3 h-3 bg-slate-900 rounded-sm align-[-2px] mr-1" />YTD pace marker — % of year elapsed · YTD cells show On pace / Behind</span>
          <span className="ml-auto">Click a MANUAL cell to enter that period's number · AUTO cells refresh live</span>
        </div>
      </div>

      {/* AI metric builder (super admin) */}
      <Dialog open={!!ai} onOpenChange={(o) => !o && setAi(null)}>
        <DialogContent className="max-w-lg rounded-sm max-h-[90vh] overflow-y-auto" data-testid="ai-metric-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Sparkles size={17} className="text-cyan-600" />AI Metric Builder</DialogTitle>
          </DialogHeader>
          {ai && !ai.proposal && (
            <div className="space-y-3">
              <p className="text-xs text-slate-500">
                Describe the number you want on the scorecard — the AI builds it live from Compass data
                (quotes, jobs, POs, CRM deals, suppliers). You review it with real numbers before it's added.
              </p>
              <Textarea rows={3} autoFocus value={ai.prompt}
                onChange={(e) => setAi((a) => ({ ...a, prompt: e.target.value }))}
                placeholder={'e.g. "Average sale price of quotes sent each week" or "% of new enquiries that are colorbond"'}
                className="rounded-sm text-sm" data-testid="ai-prompt-input" />
              {ai.error && <p className="text-xs text-red-600 bg-red-50 rounded-sm px-2.5 py-2" data-testid="ai-error">{ai.error}</p>}
              <DialogFooter>
                <Button variant="outline" className="rounded-sm" onClick={() => setAi(null)} data-testid="ai-cancel-btn">Cancel</Button>
                <Button className="bg-cyan-600 hover:bg-cyan-700 rounded-sm" data-testid="ai-build-btn"
                  disabled={(ai.prompt || "").trim().length < 5 || buildMut.isPending}
                  onClick={() => buildMut.mutate(ai.prompt.trim())}>
                  <Sparkles size={14} className="mr-1" />{buildMut.isPending ? "Building…" : "Build with AI"}
                </Button>
              </DialogFooter>
            </div>
          )}
          {ai?.proposal && (
            <div className="space-y-3" data-testid="ai-proposal">
              <div>
                <label className="text-xs uppercase tracking-wider font-semibold text-slate-500">Metric name</label>
                <Input value={ai.proposal.name} onChange={(e) => setProp({ name: e.target.value })} className="rounded-sm mt-1" data-testid="ai-name-input" />
              </div>
              {ai.explanation && (
                <p className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-sm px-2.5 py-2" data-testid="ai-explanation">{ai.explanation}</p>
              )}
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="text-xs uppercase tracking-wider font-semibold text-slate-500">Goal</label>
                  <Select value={ai.proposal.goal_op} onValueChange={(v) => setProp({ goal_op: v })}>
                    <SelectTrigger className="rounded-sm mt-1" data-testid="ai-op-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="gte">≥ at least</SelectItem>
                      <SelectItem value="lte">≤ at most</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider font-semibold text-slate-500">Target</label>
                  <Input type="number" value={ai.proposal.goal} onChange={(e) => setProp({ goal: e.target.value })} className="rounded-sm mt-1 font-mono" data-testid="ai-goal-input" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider font-semibold text-slate-500">Period</label>
                  <Select value={ai.proposal.period || "weekly"} onValueChange={(v) => setProp({ period: v })}>
                    <SelectTrigger className="rounded-sm mt-1" data-testid="ai-period-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {PERIODS.map(([k, lb]) => <SelectItem key={k} value={k}>{lb}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider font-semibold text-slate-500">Owner</label>
                <Select value={ai.proposal.owner_id || "none"} onValueChange={(v) => {
                  const m = (team || []).find((t) => t.id === v);
                  setProp({ owner_id: v === "none" ? "" : v, owner_name: m?.name || "Leadership" });
                }}>
                  <SelectTrigger className="rounded-sm mt-1" data-testid="ai-owner-select"><SelectValue placeholder="Pick owner" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Leadership (no nudges)</SelectItem>
                    {(team || []).map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div data-testid="ai-preview">
                <label className="text-xs uppercase tracking-wider font-semibold text-slate-500">Live preview — last 8 weeks</label>
                <div className="grid grid-cols-8 gap-px mt-1 bg-slate-200 border border-slate-200 rounded-sm overflow-hidden">
                  {(ai.preview?.weeks || []).map((w, i) => (
                    <div key={w.start} className={`bg-white px-1 py-1.5 text-center ${i === 7 ? "bg-cyan-50" : ""}`}>
                      <div className="text-[9px] text-slate-400 whitespace-nowrap">{wkLabel(w.start)}</div>
                      <div className="font-mono text-[11px] font-semibold text-slate-800">{fmtVal(ai.preview?.values?.[i], ai.proposal.unit)}</div>
                    </div>
                  ))}
                </div>
                <p className="text-[10.5px] text-slate-400 mt-1">Weekly preview shown — pick a period above and it re-buckets live once added.</p>
              </div>
              <DialogFooter className="flex items-center justify-between sm:justify-between">
                <Button variant="ghost" className="text-slate-500 rounded-sm" data-testid="ai-back-btn"
                  onClick={() => setAi((a) => ({ ...a, proposal: null, error: null }))}>
                  <ArrowLeft size={14} className="mr-1" />Reword
                </Button>
                <Button className="bg-cyan-600 hover:bg-cyan-700 rounded-sm" data-testid="ai-add-btn"
                  disabled={!ai.proposal.name?.trim() || ai.proposal.goal === "" || addAiMut.isPending}
                  onClick={() => addAiMut.mutate()}>
                  <Plus size={14} className="mr-1" />Add to Scorecard
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* manual value entry */}
      <Dialog open={!!cell} onOpenChange={(o) => !o && setCell(null)}>
        <DialogContent className="max-w-xs rounded-sm" data-testid="value-dialog">
          <DialogHeader><DialogTitle className="text-base">{cell?.metric?.name}</DialogTitle></DialogHeader>
          {cell && (
            <div>
              <div className="text-xs text-slate-500 mb-2">
                {PERIOD_HINT[cell.metric.period || "weekly"]} {wkLabel(cell.week)} · goal {fmtGoal(cell.metric)}
              </div>
              <Input type="number" autoFocus value={cell.value}
                onChange={(e) => setCell((c) => ({ ...c, value: e.target.value }))}
                onKeyDown={(e) => e.key === "Enter" && cell.value !== "" && valMut.mutate({ ...cell, value: parseFloat(cell.value) })}
                className="rounded-sm font-mono" data-testid="value-input" />
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" className="text-slate-500 rounded-sm" data-testid="clear-value-btn"
              onClick={() => valMut.mutate({ ...cell, value: null })}>Clear</Button>
            <Button className="bg-cyan-600 hover:bg-cyan-700 rounded-sm" data-testid="save-value-btn"
              disabled={cell?.value === "" || valMut.isPending}
              onClick={() => valMut.mutate({ ...cell, value: parseFloat(cell.value) })}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* metric add/edit */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-md rounded-sm max-h-[90vh] overflow-y-auto" data-testid="metric-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Table2 size={17} className="text-cyan-600" />{editing?.id ? "Edit Metric" : "New Metric"}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              {!editing.id && (
                <>
                  <div>
                    <label className="text-xs uppercase tracking-wider font-semibold text-slate-500">Data source</label>
                    <Select value={editing.source} onValueChange={pickSource}>
                      <SelectTrigger className="rounded-sm mt-1" data-testid="metric-source-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="manual">Manual — owner enters each period</SelectItem>
                        {(connectors || []).map((c) => <SelectItem key={c.key} value={c.key}>AUTO — {c.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-wider font-semibold text-slate-500">Reporting period</label>
                    <div className="inline-flex border border-slate-300 rounded-sm overflow-hidden mt-1" data-testid="metric-period-toggle">
                      {PERIODS.map(([k, lb]) => (
                        <button key={k} type="button" onClick={() => setForm({ period: k })} data-testid={`period-btn-${k}`}
                          className={`px-3 py-1.5 text-xs font-semibold ${editing.period === k ? "bg-slate-800 text-white" : "text-slate-500 hover:bg-slate-100"}`}>{lb}</button>
                      ))}
                    </div>
                    <p className="text-[11px] text-slate-400 mt-1">
                      {editing.period === "ytd" ? "One running figure from 1 July vs the annual goal, with a pace bar."
                        : `Tracked per ${editing.period === "weekly" ? "week (Mon–Sun)" : editing.period === "monthly" ? "calendar month" : "FY quarter (Jul–Jun)"} — the goal applies to each ${editing.period.replace("ly", "")}.`}
                    </p>
                  </div>
                </>
              )}
              <div>
                <label className="text-xs uppercase tracking-wider font-semibold text-slate-500">Metric name *</label>
                <Input value={editing.name} onChange={(e) => setForm({ name: e.target.value })} className="rounded-sm mt-1" data-testid="metric-name-input" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider font-semibold text-slate-500">Description</label>
                <Input value={editing.description} onChange={(e) => setForm({ description: e.target.value })} className="rounded-sm mt-1" data-testid="metric-desc-input" />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="text-xs uppercase tracking-wider font-semibold text-slate-500">Goal</label>
                  <Select value={editing.goal_op} onValueChange={(v) => setForm({ goal_op: v })}>
                    <SelectTrigger className="rounded-sm mt-1" data-testid="metric-op-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="gte">≥ at least</SelectItem>
                      <SelectItem value="lte">≤ at most</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider font-semibold text-slate-500">Target *</label>
                  <Input type="number" value={editing.goal} onChange={(e) => setForm({ goal: e.target.value })} className="rounded-sm mt-1 font-mono" data-testid="metric-goal-input" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider font-semibold text-slate-500">Unit</label>
                  <Select value={editing.unit} onValueChange={(v) => setForm({ unit: v })} disabled={editing.source !== "manual"}>
                    <SelectTrigger className="rounded-sm mt-1" data-testid="metric-unit-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="count">Count</SelectItem>
                      <SelectItem value="currency">$ Currency</SelectItem>
                      <SelectItem value="percent">% Percent</SelectItem>
                      <SelectItem value="hours">Hours</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider font-semibold text-slate-500">Owner *</label>
                <Select value={editing.owner_id || "none"} onValueChange={(v) => {
                  const m = (team || []).find((t) => t.id === v);
                  setForm({ owner_id: v === "none" ? "" : v, owner_name: m?.name || (v === "none" ? "Leadership" : "") });
                }}>
                  <SelectTrigger className="rounded-sm mt-1" data-testid="metric-owner-select"><SelectValue placeholder="Pick owner" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Leadership (no nudges)</SelectItem>
                    {(team || []).map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                {editing.source === "manual" && <p className="text-[11px] text-slate-400 mt-1">The owner gets a bell at the start of each period if the number isn't entered.</p>}
              </div>
            </div>
          )}
          <DialogFooter className="flex items-center justify-between sm:justify-between">
            {editing?.id ? (
              <Button variant="ghost" className="text-red-600 hover:text-red-700 hover:bg-red-50 rounded-sm" data-testid="delete-metric-btn"
                onClick={() => window.confirm("Delete this metric and its history?") && delMut.mutate(editing.id)}>
                <Trash2 size={15} className="mr-1" />Delete
              </Button>
            ) : <span />}
            <div className="flex gap-2">
              <Button variant="outline" className="rounded-sm" onClick={() => setEditing(null)} data-testid="cancel-metric-btn">Cancel</Button>
              <Button className="bg-cyan-600 hover:bg-cyan-700 rounded-sm" data-testid="save-metric-btn"
                disabled={!editing?.name?.trim() || editing?.goal === "" || !editing?.owner_name || saveMut.isPending}
                onClick={() => saveMut.mutate(editing)}>
                {editing?.id ? "Save changes" : "Add Metric"}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
